/* eslint-disable import/order */
import {
  FolderPlusIcon,
  InformationCircleIcon,
  PaperClipIcon,
  PlusCircleIcon,
} from '@heroicons/react/20/solid';
import html2pdf from 'html2pdf.js';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { InvoicePDFExport } from './InvoicePDFExport';
import { InvoiceSend } from './InvoiceSend';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { PrimaryButton } from '@components/Buttons/PrimaryButtons';
import {
  CallToActionContainer,
  CallToActionItems,
  FormButtonsContainer,
} from '@components/Container';
import {
  Details,
  MainBoxDetailsColumn,
  DataHeader,
  DetailsButton,
  DetailTableDataLeft,
  DetailTableDataCenter,
  DetailTableDataRight,
  DetailTableHeaderLeft,
  DetailTableHeaderCenter,
  DetailTableHeaderRight,
  MoreAction,
  DetailsButtonSolid,
} from '@components/Details';
import { CancelButton } from '@components/Form';
import { OkButton } from '@components/Form/OkButton';
import { GridHeaderMenuTitle } from '@components/HeaderTitle';
import { TuosLogo } from '@components/Icons';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { Table, TableContainer } from '@components/TableContainers';
import TextWrapper from '@components/TextWrapper';
import { routes } from '@config/routes';
import { useNotification } from '@hooks/useNotification';
import { billingAddressDefaultValue } from '@models/BillingAddress';
import { customerDefaultValue } from '@models/Customer';
import { invoiceDefaultValue, getStatusText } from '@models/Invoice';
import { InvoiceDetailViewModel } from '@models/InvoiceDetail';
import { InvoiceDetailSalesTaxViewModel } from '@models/InvoiceDetailSalesTax';
import { BillingAddressService } from '@services/BillingAddress';
import { CustomerService } from '@services/Customer';
import { InvoiceService } from '@services/Invoice';
import { calculateInvoiceGrandTotal } from '@utils/calculateInvoiceGrandTotal';
import { formatAsCurrency } from '@utils/numberFormat';
import { toTitleCase } from '@utils/titleCase';

import { useState, useMemo, useEffect } from 'react';
import { discountTypeSelectOptions } from '@models/common/DiscountTypeSelectOptions';

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  };

  const dateTimeString = new Date(date).toLocaleString('en-US', options);

  return `${dateTimeString}`;
};

export const InvoiceDetails = () => {
  const { showInfo, showSuccess, showError } = useNotification();
  const [data, setData] = useState(invoiceDefaultValue);
  const { id } = useParams();
  const [customer, setCustomer] = useState(customerDefaultValue);

  const [showCustomerDetails, setCustomerDetails] = useState(false);

  const [customerAddress, setCustomerAddress] = useState(
    billingAddressDefaultValue
  );

  const [showEditCustomers, setShowEditCustomers] = useState(false);

  const [showSend, setShowSend] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);

  const [invoiceDetails, setInvoiceDetails] = useState<
    InvoiceDetailViewModel[]
  >([]);

  const grandTotal = useMemo(
    () =>
      calculateInvoiceGrandTotal(
        invoiceDetails,
        data.discount,
        data.discountType,
        data.isTaxExempt
      ),
    [invoiceDetails, data.discount, data.discountType, data.isTaxExempt]
  );

  const loadInvoiceDetails = () => {
    InvoiceService.getInvoiceDetails(Number(id)).then((result) => {
      if (result.isSuccess && 'data' in result) {
        setInvoiceDetails(result.data);
      } else {
        const errorMessage = `Failed to get the invoice details. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  const loadData = () => {
    InvoiceService.getById(Number(id)).then((result) => {
      if (result.isSuccess && 'data' in result) {
        setData(result.data);
        setIsLoading(false);

        if (result.data.billingAddressID) {
          BillingAddressService.getById(result.data.billingAddressID).then(
            (addressResult) => {
              if (addressResult.isSuccess && 'data' in addressResult) {
                setCustomerAddress(addressResult.data);
              } else {
                const errorMessage = `Failed to get billing address details. ${addressResult.errorMessages}`;
                console.error(errorMessage);
                showError(errorMessage);
              }
            }
          );
        }

        if (result.data.customerID) {
          CustomerService.getById(result.data.customerID).then(
            (customerResult) => {
              if (customerResult.isSuccess && 'data' in customerResult) {
                setCustomer(customerResult.data);
              } else {
                const errorMessage = `Failed to get customer details. ${customerResult.errorMessages}`;
                console.error(errorMessage);
                showError(errorMessage);
              }
            }
          );
        }

        const successMessage = `Successfully fetched the invoice record.`;
        console.log(successMessage);
      } else {
        const errorMessage = `Failed to get the invoice record. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
        setIsLoading(true);
      }
    });
  };

  useEffect(() => {
    if (id) {
      loadData();
      loadInvoiceDetails();
    }
  }, []);

  const handleEdit = (rowId: number) => {
    navigate(`${routes.RNC_INVOICES}/${rowId}/edit`, {
      state: { from: location },
    });
  };

  const handleEditCustomer = (rowId: any) => {
    navigate(`${routes.RNC_CUSTOMERS}/${rowId}/edit`, {
      state: { from: location },
    });
  };

  const getSalesTaxesBySalesTaxID = () => {
    const salesTaxes = invoiceDetails.map((o) =>
      o.invoiceDetailSalesTaxes
        .filter((o) => o.salesTaxID != 0)
        .map((salesTaxes) => ({
          ...salesTaxes,
          salesTaxKey: `${salesTaxes.salesTaxAbbreviation} (${salesTaxes.rate}%)`,
        }))
    );

    const groupedSalesTaxes = groupBy(
      salesTaxes.flat(),
      'salesTaxKey'
    ) as InvoiceDetailSalesTaxViewModel[][];

    return groupedSalesTaxes;
  };

  const groupBy = (array: any, key: any) => {
    return array.reduce((result: any, currentValue: any) => {
      (result[currentValue[key]] = result[currentValue[key]] || []).push(
        currentValue
      );
      return result;
    }, {});
  };

  const handlePrint = (rowId: number) => {
    // TODO: Implement print functionality
    const infoMessage = `Print functionality is not implemented yet.`;
    console.log(`${infoMessage} ${rowId}`);
    showInfo(infoMessage);
  };

  const [showPDF, setShowPDF] = useState(false);

  const handleDownloadPDF = (rowId: number) => {
    const message = `Generating PDF`;
    showInfo(message);
    setTimeout(() => {
      setShowPDF(true);
      console.log(rowId);
    }, 1000);
  };

  const downloadPDF = () => {
    const element = document.getElementById('pdf-content');
    if (element) {
      html2pdf()
        .from(element)
        .set({
          margin: 0.3,
          filename: `Invoice No: ${data.invoiceNumber}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .save()
        .then(() => {
          const successMessage = 'PDF successfully downloaded.';
          console.log(successMessage);
          showSuccess(successMessage);
          setShowPDF(false);
        })
        .catch((error: any) => {
          const errorMessage = 'Error during PDF generation';
          console.error(errorMessage, error);
          showError(errorMessage);
        });
    } else {
      const errorMessage = 'No content found to download as PDF.';
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const duplicateRecord = (rowId: number) => {
    InvoiceService.duplicate(rowId).then((result) => {
      if (result.isSuccess) {
        const successMessage = `Successfully duplicated the invoice record.`;
        console.log(successMessage);
        showSuccess(successMessage);
        setTimeout(() => {
          navigate(`${routes.RNC_INVOICES}`);
        }, 1000);
      } else {
        const errorMessage = `Failed to duplicate the invoice record. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  const deleteRecord = (rowId: number) => {
    InvoiceService.delete(rowId).then((result) => {
      if (result.isSuccess) {
        const successMessage = `Successfully deleted the invoice record.`;
        console.log(successMessage);
        showSuccess(successMessage);

        setTimeout(() => {
          navigate(`${routes.RNC_INVOICES}`);
        }, 500);
      } else {
        const errorMessage = `Failed to delete the invoice record. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  const [showDelete, setDelete] = useState(false);

  const handleDelete = (rowId: number) => {
    setDelete(true);
    console.log(rowId);
  };

  if (isLoading) {
    return (
      <>
        <Loading />
      </>
    );
  } else {
    return (
      <>
        <GridHeaderMenuTitle>
          <div className="lg:text-xl md:text-lg text-sm font-bold">
            {data.invoiceNumber}
          </div>

          <div className="flex flex-row space-x-1.5">
            <MoreAction
              dropDownActions={[
                {
                  title: 'Duplicate Record',
                  onClick: () => duplicateRecord(data.id),
                },
                {
                  showTopSeparator: true,
                  title: 'Print',
                  onClick: () => handlePrint(data.id),
                },
                {
                  title: 'Export to PDF',
                  onClick: () => handleDownloadPDF(data.id),
                },
              ]}
              deleteAction={[
                {
                  title: 'Delete',
                  onClick: () => handleDelete(data.id),
                },
              ]}
            />
            <span className="lg:flex md:hidden hidden">
              <PrimaryButton
                onClick={() => {
                  navigate(`${routes.RNC_INVOICES}/create`, {
                    state: { from: location },
                  });
                }}
              >
                Create new invoice
              </PrimaryButton>
            </span>

            <button
              onClick={() => {
                navigate(`${routes.RNC_ESTIMATES}/create`, {
                  state: { from: location },
                });
              }}
              className="text-primary-500 lg:hidden md:flex flex items-center"
            >
              <PlusCircleIcon className="w-6 h-6 md:w-7 md:h-7" />
            </button>
          </div>
        </GridHeaderMenuTitle>
        <Details>
          {/* Desktop and Tablet */}
          <MainBoxDetailsColumn>
            <div className="lg:flex md:flex hidden col-span-1 grid-cols-2 lg:space-x-24 md:space-x-16">
              <div className="col-span-1">
                <DataHeader label="Status" />
                <div className="inline-flex items-center rounded-md lg:px-4 lg:py-1 md:px-2 md:py-1 lg:text-base md:text-base text-sm font-medium ring-1 ring-inset ring-primary-600/20">
                  {getStatusText(data.status)}
                </div>
              </div>
              <div className="col-span-1">
                <DataHeader label="Customer" />
                <div className="text-primary-500 lg:text-lg md:text-sm text-xs hover:text-primary-600 font-bold">
                  <button
                    className="flex lg:gap-x-2 md:gap-x-1 gap-x-0.5 items-center uppercase text-start"
                    onClick={() => setCustomerDetails(true)}
                  >
                    <div>{customer.companyName}</div>
                    <InformationCircleIcon className="lg:w-6 lg:h-6 md:w-5 md:h-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="lg:flex md:flex hidden col-span-1 grid-cols-2 lg:space-x-24 md:space-x-16">
              <div className="col-span-1">
                <DataHeader label="Invoice Amount" />
                <div className="lg:text-lg md:text-sm text-sm font-bold">
                  {formatAsCurrency(grandTotal)}
                </div>
              </div>
              <div className="col-span-1">
                <DataHeader label="Due Date" />
                <div className="lg:text-lg md:text-sm sm:text-sm">
                  {formatDate(data.dueDate)}
                </div>
              </div>
            </div>
          </MainBoxDetailsColumn>

          {/* Mobile */}
          <div className="lg:hidden md:hidden block space-y-4">
            <MainBoxDetailsColumn>
              <div className="w-full flex col-span-2 grid-cols-1 justify-between space-x-8">
                <div className="col-span-1">
                  <DataHeader label="Status" />
                  <div className="inline-flex items-center rounded-md p-1.5 text-xs font-medium ring-1 ring-inset ring-primary-600/20 whitespace-nowrap">
                    {getStatusText(data.status)}
                  </div>
                </div>
                <div className="col-span-1">
                  <DataHeader label="Customer" />
                  <div className="text-primary-500 text-xs hover:text-primary-600 font-bold">
                    <button
                      className="flex gap-x-1.5 items-start uppercase text-start"
                      onClick={() => setCustomerDetails(true)}
                    >
                      <div>{customer.companyName}</div>
                      <InformationCircleIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </MainBoxDetailsColumn>

            <MainBoxDetailsColumn>
              <div className="w-full flex col-span-2 grid-cols-1 justify-between">
                <div className="col-span-1">
                  <DataHeader label="Invoice Amount" />
                  <div className="text-xs font-semibold">
                    {formatAsCurrency(grandTotal)}
                  </div>
                </div>
                <div className="col-span-1">
                  <DataHeader label="Due Date" />
                  <div className="text-xs">{formatDate(data.dueDate)}</div>
                </div>
              </div>
            </MainBoxDetailsColumn>
          </div>

          <div className="">
            <CallToActionContainer>
              <CallToActionItems>
                <div className="space-y-3">
                  <div className="flex text-primary-500 lg:text-2xl md:text-lg text-sm items-center lg:space-x-4 md:space-x-3 space-x-2">
                    <FolderPlusIcon className="lg:w-7 lg:h-7 md:w-6 md:h-6 w-5 h-5" />
                    <div className="text-gray-400 font-normal">Create</div>
                  </div>

                  <div className="lg:text-base md:text-sm text-xs">
                    <span className="font-semibold">Created: </span>
                    {formatDateTime(data.date)}
                  </div>
                </div>

                <div>
                  <DetailsButton onClick={() => handleEdit(data.id)}>
                    Edit Invoice
                  </DetailsButton>
                </div>
              </CallToActionItems>
            </CallToActionContainer>
            <div className="w-2.5 ml-24 bg-gray-200 h-8" />
            <CallToActionContainer>
              <CallToActionItems>
                <div className="space-y-3">
                  <div className="flex text-primary-500 lg:text-2xl md:text-lg text-sm items-center lg:space-x-4 md:space-x-3 space-x-2">
                    <PaperClipIcon className="lg:w-7 lg:h-7 md:w-6 md:h-6 w-5 h-5" />
                    <div className="text-gray-400 font-normal">Send</div>
                  </div>

                  <div className="lg:text-base md:text-sm text-xs">
                    <span className="font-semibold">Last Sent: </span>
                    {data.status}
                  </div>
                </div>

                <div className="lg:flex md:flex grid justify-end lg:space-x-4 md:space-x-3 space-x-2 space-y-2">
                  <DetailsButton
                    onClick={() => {
                      undefined;
                    }}
                  >
                    Mark as Sent
                  </DetailsButton>

                  <DetailsButtonSolid
                    onClick={() => {
                      setShowSend(true);
                    }}
                  >
                    Send Invoice
                  </DetailsButtonSolid>
                </div>
              </CallToActionItems>
            </CallToActionContainer>
          </div>

          <div className="shadow-md border">
            <div className="lg:p-8 md:p-6 p-4 flex justify-between">
              {/* Desktop */}
              <div className="lg:flex md:hidden hidden items-center">
                <TuosLogo height="100" width="75" />
              </div>
              {/* Tablet */}
              <div className="md:flex lg:hidden hidden items-center">
                <TuosLogo height="75" width="56.25" />
              </div>
              {/* Mobile */}
              <div className="flex lg:hidden md:hidden items-center">
                <TuosLogo height="56.25" width="42.1875" />
              </div>
              <div className="lg:text-xl md:text-base text-sm font-semibold text-end text-gray-500">
                INVOICE
                <div className="text-black lg:text-lg md:text-base text-sm">
                  Tuos Development <br />
                  <span className="lg:text-sm md:text-sm text-xs">Canada</span>
                </div>
              </div>
            </div>

            <div className="border-t-2 border-gray-700 lg:py-4 lg:px-8 md:py-4 md:px-6 py-2 px-4">
              <div className="lg:flex md:flex block grid-cols-2 justify-between">
                <div className="lg:col-span-1 md:col-span-1 col-span-2">
                  <div className="lg:text-base md:text-sm text-sm font-semibold text-gray-500">
                    Bill to
                  </div>
                  <div className="lg:text-base md:text-sm text-sm font-semibold uppercase">
                    {customer.companyName}
                  </div>
                  <div className="lg:text-base md:text-sm text-xs">
                    <span className="uppercase">
                      {customer.primaryContact?.firstname}{' '}
                      {customer.primaryContact?.lastname}
                    </span>
                    <br />
                    {toTitleCase(
                      `${customerAddress.address1} ${customerAddress.address2}`
                    )}
                    <br />
                    {toTitleCase(
                      `${customerAddress.city}, ${customerAddress.state} ${customerAddress.postalCode}`
                    )}
                    <br />
                    {toTitleCase(`${customerAddress.country}`)}
                    <br />
                    <br />
                    {customer.primaryContact?.phone}
                    <br />
                    <span className="lowercase">
                      {customer.primaryContact?.email}
                    </span>
                  </div>
                </div>

                <div className="lg:col-span-1 md:col-span-1 lg:text-base md:text-sm text-xs col-span-2 space-y-1">
                  <div className="font-semibold w-full flex items-center justify-between space-x-4 lg:pl-4 md:pl-4 pl-0 pr-1">
                    <div>Invoice Number:</div>
                    <div className="font-normal text-end">
                      {data.invoiceNumber}
                    </div>
                  </div>
                  <div className="font-semibold w-full flex items-center justify-between space-x-4 lg:pl-4 md:pl-4 pl-0 pr-1">
                    <div>Invoice Date:</div>
                    <div className="font-normal text-end">
                      {formatDate(data.date)}
                    </div>
                  </div>
                  <div className="font-semibold w-full flex items-center justify-between space-x-4 lg:pl-4 md:pl-4 pl-0 pr-1">
                    <div>Due Date:</div>
                    <div className="font-normal text-end">
                      {formatDate(data.dueDate)}
                    </div>
                  </div>
                  <div className="font-semibold w-full flex items-center justify-between space-x-4 lg:pl-4 md:pl-4 pl-2 pr-1 bg-gray-300 py-2">
                    <div>Grand Total(CAD):</div>
                    <div className="font-normal text-end">
                      {formatAsCurrency(grandTotal)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div></div>

            <div className="">
              <div className="max-w-5x1 mx-auto">
                <TableContainer>
                  <Table>
                    <thead className="bg-primary-100">
                      <tr>
                        <DetailTableHeaderLeft>
                          <span className="pl-2">Item</span>
                        </DetailTableHeaderLeft>

                        <DetailTableHeaderCenter></DetailTableHeaderCenter>
                        <DetailTableHeaderCenter>
                          Quantity
                        </DetailTableHeaderCenter>
                        <DetailTableHeaderCenter>Price</DetailTableHeaderCenter>
                        <DetailTableHeaderRight>
                          <span className="pr-2">Amount</span>
                        </DetailTableHeaderRight>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceDetails.map((row, index) => (
                        <tr
                          key={index}
                          className={`${
                            index % 2 === 0 ? ' bg-white' : 'bg-green-50'
                          }`}
                        >
                          <DetailTableDataLeft>
                            <div className="pl-2 ">
                              <div className="font-semibold text-start uppercase">
                                <TextWrapper text={row.product?.name || ''} />
                              </div>
                            </div>
                            <div className="font-light pl-2">
                              <TextWrapper text={row.description || ''} />
                            </div>
                          </DetailTableDataLeft>
                          <DetailTableDataCenter></DetailTableDataCenter>
                          <DetailTableDataCenter>
                            <span className="font-medium">{row.quantity}</span>
                          </DetailTableDataCenter>
                          <DetailTableDataCenter>
                            <span className="font-medium">
                              {formatAsCurrency(row.price)}
                            </span>
                          </DetailTableDataCenter>
                          <DetailTableDataRight>
                            <span className="font-medium">
                              {formatAsCurrency(row.subTotal)}
                            </span>
                          </DetailTableDataRight>
                        </tr>
                      ))}

                      <Space />

                      <tr>
                        <td colSpan={6}>
                          <div className="space-y-1">
                            <div className="flex justify-end mr-6 lg:text-sm md:text-sm text-xs">
                              <div className="font-bold mr-4">SubTotal:</div>

                              <div className="w-24 text-right">
                                {formatAsCurrency(
                                  invoiceDetails.reduce(
                                    (acc, cur) => acc + cur.subTotal,
                                    0
                                  )
                                )}
                              </div>
                            </div>

                            <div className="flex justify-end mr-6 lg:text-sm md:text-sm text-xs">
                              <div className="font-bold mr-4">Discount:</div>

                              <div className="w-24 text-right">
                                - {formatAsCurrency(data.discount)} (
                                {discountTypeSelectOptions.find(
                                  (opt) =>
                                    opt.value === String(data.discountType)
                                )?.text || 'Amount'}
                                )
                              </div>
                            </div>

                            {!data.isTaxExempt && (
                              <>
                                {Object.entries(
                                  getSalesTaxesBySalesTaxID()
                                ).map(([taxTitle, details]) => (
                                  <div
                                    className="flex justify-end mr-6 lg:text-sm md:text-sm text-xs"
                                    key={taxTitle}
                                  >
                                    <div className="font-medium mr-4">
                                      {taxTitle}
                                    </div>

                                    <div className="w-24 text-right">
                                      {formatAsCurrency(
                                        details.reduce(
                                          (acc, cur) =>
                                            acc + cur.salesTaxAmount,
                                          0
                                        )
                                      )}
                                    </div>
                                  </div>
                                ))}

                                <div className="flex justify-end mr-6 pt-2 pl-96 lg:text-sm md:text-sm text-xs">
                                  <div className="w-full ml-96 flex justify-end border-t border-gray-600 py-2 border-b-4">
                                    <div className="font-bold mr-4">
                                      Total Tax:
                                    </div>
                                    <div className="w-24 text-right">
                                      {formatAsCurrency(
                                        invoiceDetails
                                          .map((o) =>
                                            o.invoiceDetailSalesTaxes.reduce(
                                              (acc, cur) =>
                                                acc + cur.salesTaxAmount,
                                              0
                                            )
                                          )
                                          .reduce((acc, cur) => acc + cur, 0)
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            {data.isTaxExempt && (
                              <div className="flex justify-end mr-6 lg:text-sm md:text-sm text-xs">
                                <div className="font-medium mr-4 text-blue-600">
                                  Tax Exempt Invoice
                                </div>
                                <div className="w-24 text-right">
                                  {formatAsCurrency(0)}
                                </div>
                              </div>
                            )}

                            <div className="flex justify-end mr-6 lg:text-sm md:text-sm text-xs py-4">
                              <div className="font-bold mr-4">Grand Total:</div>
                              <div className="w-24 text-right">
                                {formatAsCurrency(grandTotal)}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          </div>
        </Details>

        {showCustomerDetails && (
          <>
            <Modal show={true}>
              <div className="flex pl-4 justify-between items-center">
                <div className="w-full lg:text-base md:text-sm text-sm">
                  Customer Details
                </div>
                <div>
                  <CloseButton onClick={() => setCustomerDetails(false)} />
                </div>
              </div>
              <Box>
                <div className="px-6 pt-2 pb-8 space-y-6">
                  <div>
                    <div className="font-medium text-medium">
                      {customer.companyName}
                    </div>
                    <div className="text-medium">
                      {customer.primaryContact?.firstname}{' '}
                      {customer.primaryContact?.lastname} <br />
                      {customer.primaryContact?.email}
                    </div>
                    <br />
                    <div className="text-medium">
                      Tel: {customer.primaryContact?.phone}
                    </div>
                  </div>

                  <div className="flex space-x-5">
                    <DetailsButton
                      onClick={() => handleEditCustomer(data.customerID)}
                    >
                      Edit Details
                    </DetailsButton>

                    <DetailsButton
                      onClick={() => {
                        navigate(`${routes.RNC_CUSTOMERS}/${data.customerID}`);
                      }}
                    >
                      View Profile
                    </DetailsButton>
                  </div>
                </div>
              </Box>
            </Modal>
          </>
        )}

        {showEditCustomers && (
          <>
            <Modal show={true}>
              <div className="flex pl-4 justify-between items-center">
                <div className="w-full">Edit Customer Details</div>
                <div>
                  <CloseButton onClick={() => setShowEditCustomers(false)} />
                </div>
              </div>
            </Modal>
          </>
        )}

        {showSend && (
          <>
            <Modal show={true}>
              <InvoiceSend
                onDownload={() => downloadPDF()}
                invoiceId={data.id}
                invoiceName={data.invoiceNumber}
                onClose={() => setShowSend(false)}
              />
            </Modal>
          </>
        )}

        {showDelete && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setDelete(false)} />

              <Box size="medium">
                <div className="px-4">
                  <span>Are you sure to delete this invoice?</span>
                </div>

                <FormButtonsContainer>
                  <div className="space-x-2">
                    <CancelButton onClick={() => setDelete(false)} />
                    <OkButton onClick={() => deleteRecord(data.id)}>
                      Delete
                    </OkButton>
                  </div>
                </FormButtonsContainer>
              </Box>
            </Modal>
          </>
        )}

        {showPDF && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setShowPDF(false)} />

              <Box size="medium">
                <div className="px-4 pb-2">
                  <span>Your invoice is now ready for download</span>
                </div>

                <FormButtonsContainer>
                  <div className="space-x-2">
                    <CancelButton onClick={() => setShowPDF(false)} />
                    <OkButton onClick={() => downloadPDF()}>Download</OkButton>
                  </div>
                </FormButtonsContainer>
              </Box>
            </Modal>
          </>
        )}

        {/* pdf export */}
        <InvoicePDFExport invoicesId={data.id} />
      </>
    );
  }
};
